/**
 * Every tunable in the project, in one mutable object.
 *
 * Phase 8 wires sliders directly onto these fields, so they must stay plain
 * numbers on a flat-ish object and must be read at use time rather than
 * destructured into module scope at import time. A threshold captured in a
 * closure is a threshold you cannot retune against your own voice without a
 * rebuild, and you will retune all of them.
 *
 * Sources: the constants SPEC1.md fixes outright are marked with their phase.
 * Anything not in the spec is a starting guess and is labelled as such.
 */

export const config = {
  // ── Timeline ────────────────────────────────────────────────────────────
  // Global display delay. Everything is rendered this far behind reality so
  // that slow detectors (ASR, ~300 ms) and fast ones (plosives, ~15 ms) can be
  // placed on one honest timeline. Phase 8 exposes this as a slider; the trace
  // canvas in Phase 3 is how you calibrate it.
  LAG: 320,
  LAG_MIN: 120,
  LAG_MAX: 640,

  // Per-detector latency, ms. Subtracted from each event's own timestamp before
  // LAG is applied. Rule 2 of the architecture section: get these wrong and
  // gestures appear ahead of the words that preceded them.
  latency: {
    asr: 300, // spec, Phase 2
    trill: 340, // spec, Phase 5 — half the 683 ms analysis window
    plosive: 15, // spec, Phase 6
    click: 15, // guess — same transient path as plosive
    fricative: 40, // guess — needs 2 hops of hysteresis to commit
    whistle: 40, // guess
    growl: 40, // guess
    raspberry: 40, // guess
    vision: 0, // landmarks are timestamped at capture
  },

  // Ring buffer capacities, in samples. Sized to comfortably outlive LAG_MAX
  // plus the longest analysis window.
  buffers: {
    mouth: 256, // ~8.5 s at 30 Hz
    envelope: 4096, // ~11 s at 375 Hz
    audio: 512, // ~8.5 s of rAF-rate feature frames
  },

  // ── Vision (Phase 1) ────────────────────────────────────────────────────
  vision: {
    // Landmark indices. Verify against the canonical mesh diagram — wrong
    // indices fail silently, they do not throw.
    idx: {
      eyeL: 33,
      eyeR: 263, // inter-ocular, the scale reference everything divides by
      cornerL: 61,
      cornerR: 291, // mouth corners -> width
      lipUpperInner: 13,
      lipLowerInner: 14, // inner lip centres -> gap
      lipUpperOuter: 0, // upper lip apex -> lipJitter
    },

    // Blendshape category names, verified against the label list inside
    // face_landmarker.task rather than typed from memory. Read by name, never
    // by index: the model emits 52 of these and a shifted index would return a
    // real number from the wrong muscle.
    //
    // Left/right pairs are averaged. Asymmetry is real on most faces but it is
    // not information this project uses.
    shapes: {
      jawOpen: ['jawOpen'],
      pucker: ['mouthPucker'],
      funnel: ['mouthFunnel'],
      close: ['mouthClose'],
      press: ['mouthPressLeft', 'mouthPressRight'],
      smile: ['mouthSmileLeft', 'mouthSmileRight'],
      stretch: ['mouthStretchLeft', 'mouthStretchRight'],
      lowerDown: ['mouthLowerDownLeft', 'mouthLowerDownRight'],
    },
    // aperture = clamp01(APERTURE_JAW*(jawOpen/JAW_FULL) + APERTURE_GEOM*(gap/GAP_FULL))
    // The blend matters: jawOpen alone is noisy on wide vowels, geometry alone
    // drifts with head pitch.
    APERTURE_JAW: 0.55,
    APERTURE_GEOM: 0.45,
    GAP_FULL: 0.55, // normalised lip gap that counts as fully open
    // The blendshape value that counts as a fully open jaw.
    //
    // SPEC1.md uses jawOpen raw, on the assumption it spans 0..1. It does not.
    // Across two recordings it peaked at 0.562 and 0.541 on a deliberately wide
    // "ah", so the jaw term could contribute at most ~0.31 of its 0.55 and
    // aperture had a structural ceiling near 0.76 — which put the spec's own
    // 0.7 acceptance threshold inside the noise at the top of the range, where
    // it passed one take and failed the next on the same face.
    //
    // Normalising by the value a real jaw actually reaches makes the top of the
    // scale mean something. Fitted from those two takes: "ah" moves 0.711 ->
    // 0.884 and 0.638 -> 0.791, while rest, "meep" and a lip trill stay put.
    JAW_FULL: 0.6,

    // Width needs adaptive min/max calibration — absolute corner distance
    // differs per face and per distance from camera.
    WIDTH_DECAY: 0.0006, // per frame, toward current
    WIDTH_GEOM_MIX: 0.6, // 60/40 geometry vs blendshape estimate
    // Until the observed corner distance has spanned at least this much, there
    // is no range to normalise against and the geometric estimate is noise. The
    // blendshape estimate carries width alone until then.
    WIDTH_MIN_SPAN: 0.04,
    WIDTH_SHAPE_GAIN: 1.0, // (smile+stretch)/2 vs (pucker+funnel)/2 around 0.5

    // Milliseconds without a video frame before the camera is declared dead.
    //
    // The same lesson as the audio graph, learned again in the other channel. A
    // recorded take lost its camera at 29.0 s of a 51.4 s recording and logged
    // nothing at all: `requestVideoFrameCallback` simply stopped firing,
    // `landmarker.state` stayed 'running', and `lastLandmarks` kept its final
    // value — so the overlay went on drawing a face that had stopped existing.
    // On a phone this is the screen dimming, which is why recordings now hold a
    // wake lock.
    //
    // Generous: frames arrive every 33-77 ms depending on the device, so this is
    // fifteen to thirty missed ones.
    STALL_MS: 1000,

    JITTER_WINDOW: 6, // frames of upper-lip y kept for the curvature measure
    // Mean-square second difference that counts as fully jittering.
    //
    // The first version of this measured variance over the window and used
    // 2e-5, which the first real recording showed to be saturated on every
    // sound tested — rest sat at 0.14 and everything else, trill included,
    // pegged at 1.0. Both the measure and the constant changed as a result;
    // see mouthfeatures.js for why curvature rather than variance.
    //
    // This value is a provisional fit, not a measurement. `lipOffset` is now
    // recorded raw, so the next session with a marked trill in it settles the
    // number properly instead of by argument.
    JITTER_FULL: 1e-3,
  },

  // ── Transcript (Phase 2) ────────────────────────────────────────────────
  speech: {
    lang: 'en-US',
    // Multiple words often arrive in one onresult event and were not all spoken
    // at the sampling point — they get laid out backwards from it.
    WORD_DUR_MIN: 120, // ms
    WORD_DUR_PER_CHAR: 70, // ms
    CHAR_SMOOTH: 3, // moving-average window, so the line reads as a wave
    RESTART_DELAY: 220, // ms — continuous still stops on silence
  },

  // ── Render (Phase 3 / 7) ────────────────────────────────────────────────
  render: {
    // Roboto Flex axes. Two axes from two independent signals is what makes it
    // read as a mouth signature rather than one value smeared across a line.
    WGHT_MIN: 200,
    WGHT_MAX: 900, // <- aperture
    WDTH_MIN: 75,
    WDTH_MAX: 125, // <- width
    // GRAD changes apparent weight WITHOUT changing metrics, so it is safe to
    // animate on live text. Never use wght for voice quality.
    GRAD_MIN: -200,
    GRAD_MAX: 150, // <- flatness
    // Flatness values that map to fully clean and fully noisy. Fitted loosely
    // from recordings: a voiced vowel sits around 0.01-0.05, a fricative
    // reaches 0.3, a raspberry higher. The band the features are computed over
    // keeps these well below the textbook 0..1 range — see BAND_LO/BAND_HI.
    GRAD_FLATNESS_LO: 0.03,
    GRAD_FLATNESS_HI: 0.35,
    TRACE_SECONDS: 4, // oscilloscope window

    // The gesture marks — Channel B's output, drawn in accent so they read as a
    // different voice from the words.
    //
    // Off is a real setting rather than a debug convenience. A take with plosives
    // firing on ordinary speech puts a mark between every other word, and at that
    // density they stop being punctuation and become the texture of the line. The
    // detector keeps running and the recording keeps every event either way —
    // this hides them, it does not switch Channel B off.
    GESTURES: true,

    // ── The face view (/perform?view=face) ────────────────────────────────
    // Type size as a multiple of the inter-ocular distance, in screen pixels.
    //
    // Tied to the face rather than to the viewport so the type is the same size
    // *relative to the speaker* however close they sit — which is the only way
    // the line stays anchored to a mouth rather than merely starting near one.
    // It also means leaning in makes the words grow, which is a real thing the
    // instrument is saying about distance.
    FACE_TYPE_SCALE: 1.1,
    // Smoothing on the mouth anchor, per frame toward the measurement.
    //
    // Landmarks jitter by a pixel or two even on a still face, and type pinned
    // directly to them shimmers. Low enough to sit still, high enough that the
    // line does not swim behind a turning head — a little lag reads as the text
    // trailing out, which is the intent.
    FACE_ANCHOR_EASE: 0.25,
    // Furthest the mouth may move between two camera frames, in inter-ocular
    // units. Anything further is not a measurement.
    //
    // MediaPipe blows up more often than its reputation suggests: on a measured
    // desktop take 27.8% of frames reported the inner lips further apart than a
    // whole face, in runs of up to 125 frames — six seconds at that camera rate.
    // The anchor followed every one, which is what "the crosshair keeps
    // wandering off my mouth" turned out to be. 0 disables the rejection.
    FACE_ANCHOR_JUMP: 0.6,
    // Consecutive refusals before the anchor believes the jump anyway. Without
    // it, a genuinely fast move or somebody else stepping in front of the camera
    // would be rejected for the rest of the take.
    FACE_ANCHOR_GIVE_UP: 8,
    // How far below the lip line the text sits, in inter-ocular units. Zero puts
    // it across the mouth itself, which covers the thing you are watching.
    FACE_DROP: 0.35,
    // Brightness the camera feed is knocked back to, so white type reads over a
    // lit face. The picture is context; the typography is the piece.
    FACE_DIM: 0.45,
  },

  // ── The mouth as an emitter ─────────────────────────────────────────────
  // Words leave the lips and drift, instead of hanging together in a line.
  //
  // Everything here that describes a distance is expressed in inter-ocular
  // units per second rather than in pixels, for the same reason the type size
  // is: the behaviour has to be a property of the speaker rather than of the
  // window, or the same performance looks frantic on a phone and sluggish on a
  // monitor.
  emit: {
    LIFE: 2.6, // seconds from the lips to gone
    // Launch speed at full aperture. Scaled by how far the mouth was actually
    // open at the moment the word was spoken — a shout throws its words further
    // than a mutter, which is a real thing the vision channel already knows.
    SPEED: 0.55,
    ANGLE: -90, // degrees; -90 is straight up, 0 is screen-right
    SPREAD: 55, // half-angle of the cone, so words do not stack in a column
    GRAVITY: 0.15, // iod/s² downward. Slight, so the arc is felt not watched
    DRAG: 0.35, // fraction of velocity retained per second
    // Words grow as they travel, which is what reads as coming *toward* the
    // viewer rather than sliding across a picture of a face.
    SCALE_FROM: 0.55,
    SCALE_TO: 1.25,
    MAX: 90, // live words; beyond this the oldest are retired early
    // Chrome finalises a whole phrase at once, so ten words can arrive in one
    // event having been spoken over four seconds. They are scheduled by when
    // they were *said*, and this is the floor on the gap between two leaving the
    // mouth — without it a late finalisation fires the lot as a single clump.
    MIN_GAP: 90,
    // One particle per character rather than per word.
    //
    // A word is the unit ASR reports; a letter is the unit the mouth actually
    // made, and Phase 2 already resolved each one against the face at its own
    // instant. So a letter can be launched at the moment it was spoken, with a
    // speed taken from the same aperture that set its weight — one reading of
    // one mouth driving both, which is the claim the whole project rests on.
    PER_LETTER: true,
    // The floor between two letters. MIN_GAP is a *word* gap: at
    // speech.WORD_DUR_PER_CHAR the characters of one word are ~70 ms apart, so
    // 90 would throttle a word down to a trickle and undo the spacing that
    // makes it read as speech rather than as a burst.
    LETTER_GAP: 20,

    // ── Where the stream comes out ──────────────────────────────────────
    // Offset from the lip centroid, in inter-ocular units like everything else
    // here, so moving closer to the camera does not slide the launch point off
    // the face. Zero is the lip opening itself.
    //
    // X is screen-right and Y is screen-down — what you see, not what the
    // camera saw. The feed is mirrored and facemap.js has already undone that
    // by the time these are applied, so a positive X moves the stream toward
    // the right of the picture in front of you.
    ORIGIN_X: 0,
    ORIGIN_Y: 0,
    // Random radius around that point. At zero every letter starts on exactly
    // the same pixel and a fast phrase reads as a single seam; a little breaks
    // the column up without the origin stopping being the mouth.
    JITTER: 0,
    // The floor under the aperture term in the launch speed. A consonant-heavy
    // word can be spoken with a mouth that barely opens, and a word with no
    // velocity does not leave — it sits on the lips and fades, which looks like
    // a bug rather than like a quiet word. At 1 aperture stops mattering.
    DRIVE_FLOOR: 0.35,

    // ── How it looks ────────────────────────────────────────────────────
    // Type size at launch is SCALE_FROM × TYPE_SCALE × iod. Two constants
    // because SCALE_FROM is also the start of the growth curve, and pulling
    // them apart is what lets the type be bigger without also changing how much
    // it grows on the way out.
    TYPE_SCALE: 1.1,
    // Fraction of a life spent fading in, and the exponent on the fade out.
    // The asymmetry is the point: a word has to be legible for most of its
    // travel or the whole thing is a texture rather than a sentence.
    //
    // The fade out is `(1 - t)^FADE_OUT`, and the base is below 1 for the whole
    // life — so a *larger* exponent is dimmer everywhere and starts going at
    // once, and below 1 holds bright and drops at the very end. Easy to get
    // backwards, which is why emitter.test.js pins the direction.
    FADE_IN: 0.06,
    FADE_OUT: 1.6,
    // Ceiling on the whole stream, for reading type over a bright face.
    OPACITY: 1,
    // Tumble, degrees per second, random sign per particle. Zero is upright.
    // Derived from age rather than integrated, so it costs no particle state.
    SPIN: 0,
  },

  // ── The alignment probe ─────────────────────────────────────────────────
  // One letter, held at the lips, not going anywhere.
  //
  // The anchor is the one thing on this page that cannot be checked by reading
  // the code: every landmark index can be right and the launch point still sit
  // on the forehead, because a box was measured against the wrong element. That
  // is not hypothetical — it is what `boxFor` exists to have caught. A marker on
  // the lip centroid makes the error visible in the only place it is real.
  //
  // The glyph beside it carries the live axes, so the same object answers the
  // second question too: whether aperture and width are reaching the type. It is
  // the letter stream with the motion removed.
  probe: {
    ON: true,
    // The two halves, separately. They answer different questions — the
    // crosshair is "is the anchor on my lips", the glyph is "are the axes
    // reaching the type" — and once you have satisfied yourself about one of
    // them the other is still worth watching. Both off is the same as ON: false.
    CROSS: true,
    GLYPH: true,
    // Where the type will come out, which is not where the mouth is once
    // emit.ORIGIN_X/Y have been moved. Positioning a letter stream by watching
    // letters is positioning it a second and a half after the decision; this
    // shows the launch point and the scatter around it before anything is said.
    EMITTER: true,
    SIZE: 0.3, // crosshair, ×iod
    // The whole probe, relative to the lip centroid. In inter-ocular units like
    // everything else, so a nudge that lines it up stays lined up when the
    // speaker leans in. X is screen-right and Y is screen-down: the mirror has
    // already been undone by the time these apply.
    X: 0,
    Y: 0,
    // The glyph, relative to the crosshair. OFFSET is the horizontal one and
    // keeps its name because it is already in saved settings and shared URLs.
    OFFSET: 0.55,
    GLYPH_Y: 0,
    // A string, so it deliberately gets no slider — tunables.js has numbers and
    // booleans, and which letter to draw is not a threshold anyone retunes by
    // ear. 'A' because it is wide open: aperture has somewhere to go from there.
    LETTER: 'A',
  },

  // ── Audio capture (Phase 4) ─────────────────────────────────────────────
  audio: {
    // NON-NEGOTIABLE. Chrome's noise suppressor classifies trills and
    // raspberries as noise and removes them; you detect nothing and it looks
    // like the DSP is broken. AGC flattens the dynamics being measured.
    constraints: {
      noiseSuppression: false,
      autoGainControl: false,
      echoCancellation: false,
    },
    fftSize: 2048, // AnalyserNode, for spectral features
    smoothingTimeConstant: 0, // we do our own smoothing; the built-in lies about transients
    WORKLET_BATCH: 8, // blocks per postMessage, to avoid flooding the main thread
    ATTACK_WINDOW: 20, // ms, for the dB delta
    SILENCE_DB: -55, // below this, skip classification entirely

    // Band the spectral features are computed over, Hz.
    //
    // Not in SPEC1.md, and not optional. Flatness over the full spectrum of a
    // 48 kHz capture is dominated by the near-empty top octaves, where every
    // bin sits on the noise floor — which is *flat*. A raspberry, whose energy
    // is all below ~3 kHz, would then read as less flat than the silence
    // around it, destroying the single best discriminator in the project.
    //
    // 100 Hz drops mains hum and handling rumble; 8 kHz keeps the whole of a
    // fricative's energy and the whole whistle range.
    BAND_LO: 100,
    BAND_HI: 8000,

    // Zero-crossing rate is normalised against this, not against Nyquist.
    //
    // SPEC1.md calls ZCR "0..1 normalised" and puts the fricative threshold at
    // 0.6. Normalising by Nyquist — crossings per sample, the textbook
    // definition — makes 0.6 mean "dominant energy near 14 kHz", which no /s/
    // reaches at 48 kHz. It also makes the feature sample-rate dependent: the
    // same "sss" reads 0.25 on a 48 kHz device and 0.75 on a 16 kHz one, so a
    // threshold fitted on one machine is wrong on the next.
    //
    // Halving the crossing rate gives the dominant frequency; dividing by a
    // fixed 8 kHz reference makes the number mean the same thing everywhere and
    // puts a real fricative around 0.7, which is what the spec's threshold was
    // written against.
    ZCR_REF_HZ: 8000,

    // Autocorrelation search range, Hz. Wider than any single detector needs:
    // growl looks below 80 and whistle up to 3000, so the estimator has to be
    // able to report outside both or the thresholds test their own edges.
    F0_MIN: 55,
    F0_MAX: 3200,
    // Lag step for the first autocorrelation pass, refined to 1 around the peak.
    // A normalised ACF peak is about half a period wide, so a coarse pass cannot
    // step over one — and it turns ~2 million multiply-adds per frame into
    // ~400 thousand, which is the difference between this running on rAF and not.
    ACF_COARSE: 4,
    // The adaptive noise gate. See noisefloor.js — every level threshold in
    // Channel B is really a claim about the gap between a sound and the
    // background it sits in, and only one of the two can be known in advance.
    NOISE: {
      SECONDS: 3, // history the floor is estimated over
      // Envelope samples per block minimum, ~85 ms. Must span at least a couple
      // of periods of anything that should count as background: the block
      // minimum only finds the quiet part of a pulsing sound if a quiet part
      // falls inside the block. At 85 ms the slowest thing in the trill band
      // (18 Hz, 56 ms) fits comfortably.
      BLOCK: 32,
      // How far above the background a sound has to be to be described.
      //
      // The one number here that is still a guess. It could not be fitted from
      // the recording that motivated the whole mechanism, because that take had
      // music playing throughout — deliberately, to make it harder — so it
      // contains no quiet to measure against. A take with a few seconds of
      // genuine silence in it settles this.
      MARGIN_DB: 8,
      // Used until enough history exists. Low on purpose: a gate that has not
      // measured anything yet must not be discarding data.
      FALLBACK_DB: -70,
    },
    // Absolute backstop, below the reach of any adaptive estimate. Digital
    // silence has no features and no floor worth tracking.
    FEATURE_FLOOR_DB: -100,
    // Milliseconds without an envelope batch before the graph is declared dead.
    //
    // This exists because of a recorded session where it was not there. Chrome's
    // SpeechRecognition restarted mid-take, took the input device, and suspended
    // our AudioContext. The worklet stopped, so the envelope stopped — but the
    // AnalyserNode kept its last buffer, `capture.running` was still true, and
    // the analyser went on producing frames from it. Twenty-three seconds of
    // byte-identical flatness, ZCR, f0 and periodicity were recorded as
    // measurements. Nothing anywhere reported a problem.
    //
    // Generous, because the main thread genuinely blocks: the same recording has
    // a 1.28 s gap where MediaPipe and the device change ran together. A false
    // positive costs one dropped frame; a false negative costs a fabricated
    // dataset.
    STALL_MS: 750,

    // Clock alignment: how many envelope batches the audio->performance.now()
    // offset estimator keeps. See clock.js — it takes the minimum over this
    // window, and the window has to be long enough to contain one message that
    // was not delayed. ~2.7 s at 48 kHz.
    CLOCK_WINDOW: 128,
  },

  // ── Trill detection (Phase 5) ───────────────────────────────────────────
  // FFT the amplitude ENVELOPE, not the audio. FFT-ing raw audio finds pitch;
  // a trill's identity is in how fast the envelope pulses.
  //
  // Envelope rate is sampleRate/128 (~375 Hz at 48 kHz) and is computed at
  // runtime — never hardcoded, sample rates vary by device.
  envelope: {
    FFT_SIZE: 256, // -> 683 ms window at 375 Hz
    HOP_BLOCKS: 32, // ~85 ms
    BAND_LO: 18, // Hz
    BAND_HI: 36, // Hz
    // How far the peak must stand out of its own band.
    //
    // SPEC1.md says 3. The only lip trill on record peaked at 2.97 and dipped
    // to 2.00 across its four windows, so 3 rejects it outright. 1.8 sits below
    // its worst window with a little room — deliberately not at 2.0, because a
    // threshold placed exactly on the one measurement it was derived from is a
    // coin toss dressed as a constant.
    //
    // A weak gate regardless. Stability does the work; see envelope.js.
    PEAK_OVER_MEDIAN: 1.8,
  },

  // ── Gesture classification (Phase 6) ────────────────────────────────────
  // Checked in this order: closures and transients before sustained states.
  // Every one of these is adjustable at runtime by design.
  gesture: {
    // Trill (Phase 5). The rate is what identifies it, and the *stability* of
    // that rate is what distinguishes it from the largest bin of some noise.
    trill: {
      // Hz of spread across the stability window that takes stability to zero.
      // A recorded lip trill held within 0.6 Hz over four hops; nothing else in
      // the take held within 3.6. SPEC1.md's own acceptance criterion is +/-3 Hz
      // across a 1 s sustain, which is where this comes from.
      RATE_TOLERANCE: 3,
      // How many consecutive hops have to agree, and how many agreeing hops in
      // a row commit a trill.
      //
      // Both come from a parameter sweep over a real recording, scored on three
      // things at once: does it still find the marked trill, how much synthetic
      // broadband noise gets through, and how often it fires during 92 seconds
      // of a room with music playing.
      //
      //   stab  hyst | room events   trill found   synthetic noise
      //      3     2 |         67           yes                  3
      //      3     3 |         37           yes                  1
      //      4     3 |         19           yes                  1
      //      5     3 |         17           yes                  0     <- here
      //      6     2 |         16           NO                   0
      //      6     3 |          8           NO                   0
      //
      // 5/3 is the last row that still finds the trill. Everything quieter
      // loses it. That is not a tuning failure, it is where this method runs
      // out — see the note above the class in envelope.js.
      STABILITY_HOPS: 5,
      // Trill gets its own hysteresis rather than the shared gesture one. The
      // spec's 2 stands for the Phase 6 detectors; this needs 3, and silently
      // widening the shared constant would retune six other things.
      HYSTERESIS_HOPS: 3,
      // Fraction of the tolerance a candidate has to reach. 0.5 corresponds to
      // a spread of 1.5 Hz across those hops — twice as tight as the spec's
      // acceptance, and still well looser than the recorded trill managed.
      MIN_STABILITY: 0.5,
      // How much the loudness has to pulse, as a fraction of its own mean.
      //
      // Replaces SPEC1.md's PEAK_MIN_MAG, which is an absolute in envelope
      // units — a statement about microphone gain and how far away the speaker
      // sat, not about the sound. Depth is a property of the sound: 0 is
      // steady, 1 goes fully silent between pulses.
      //
      // A floor, not a discriminator, and the recording is why. It was set at
      // 0.15 on the theory that a lip trill interrupts the airflow and so
      // modulates deeply. In the take it scored 0.08 — lower than the whistle's
      // 0.17 — because the speaker had music playing, and background inflates
      // the mean this is divided by without contributing to the peak. The idea
      // may still be right in a quiet room; it is not evidence today.
      //
      // What it does earn its place doing is rejecting a perfectly steady
      // sound, which has no modulation at all and which the other two gates
      // cannot see (a signal with no energy has no meaningful peak-to-median).
      MIN_DEPTH: 0.05,
    },
    // SPEC1.md says minFlatness 0.5. Measured across a take containing four
    // "papa"s, the loudest burst reached 0.090 and rest sat at 0.048 — so 0.5
    // rejects every plosive there has ever been on this project.
    //
    // The reason is the window, not the threshold. An AudioFrame is 2048
    // samples, 43 ms at 48 kHz; a /p/ burst is nearer 5 ms. Seven eighths of
    // the frame that contains a burst is the vowel around it, so the burst's
    // broadband content is diluted almost out of existence before flatness is
    // computed. No threshold fixes that.
    //
    // 0.05 is what the data supports: enough to reject a pure tone or a thump
    // on the desk, not enough to pretend it is identifying anything. Attack and
    // the vision release gate carry the plosive.
    plosive: { attackDb: 15, maxDur: 80, minFlatness: 0.05 },
    whistle: { f0Lo: 800, f0Hi: 3000, minPeriodicity: 0.85, maxFlatness: 0.2 },
    // maxFlatness is not in the spec's table and is not optional: without it a
    // sustained "sss" is classified as a growl. Autocorrelation on broadband
    // noise returns some low frequency with low periodicity, which is exactly
    // the growl condition — the spec's rule describes a growl perfectly and
    // describes a fricative just as well. A growl has its energy concentrated
    // low; a fricative is broadband. That is the difference.
    growl: { f0Max: 80, maxPeriodicity: 0.5, minRms: 0.02, maxFlatness: 0.4 },
    // minFlatness is 0.15, not the spec's 0.5, for the same reason the band
    // exists at all. Flatness here is measured over 100 Hz - 8 kHz; an "sss"
    // has its energy concentrated around 5-8 kHz, which is *inside* that band
    // and therefore not flat across it. Measured: sss reached 0.307 at its
    // peak with a median of 0.180, against 0.023 at rest. It separates, just
    // nowhere near 0.5.
    //
    // ZCR is the real discriminator for a fricative and it works as specified,
    // peaking at 0.847 on that same "sss". Flatness is the supporting gate.
    fricative: { minZcr: 0.6, minFlatness: 0.15, minDur: 150 },
    click: { attackDb: 20, maxDur: 40 },
    raspberry: { minFlatness: 0.7, minDur: 200 },
    // How long a sustained state must hold before it is believed.
    //
    // SPEC1.md says two consecutive analysis hops. Its hops are the envelope's,
    // 85 ms apart; these frames arrive on rAF, which has been anywhere between
    // 13 and 60 Hz on this project's own recordings depending on main-thread
    // load. A frame count would make the classifier's behaviour a property of
    // the machine, so this is the same idea in milliseconds.
    HYSTERESIS_MS: 60,

    // Vision. Not the confidence tweak SPEC1.md describes — see gestures.js for
    // why it ended up load-bearing instead.
    vision: {
      // Aperture at or below which the mouth counts as shut. Rest sits around
      // 0.012 and a wide "ah" reaches 0.88 on the recordings so far.
      CLOSED_APERTURE: 0.12,
      // Aperture rise across a burst that counts as a release. The measurable
      // half of a bilabial closure: the closure itself looks exactly like a
      // mouth at rest, because it is one.
      RELEASE_RISE: 0.15,
      RELEASE_LOOKBACK: 100, // ms before the burst
      RELEASE_WINDOW: 120, // ms after it
      // Standard deviation of aperture over a gesture's span, below which the
      // face was not doing anything and the sound was not the speaker's.
      //
      // This is the veto that stands between Channel B and a stereo, and it is
      // a starting guess: rest measured 0.012-0.017 of aperture *level*, but
      // the spread of a still face over a few hundred milliseconds has not been
      // measured yet. The take that settles it is a quiet room with the camera
      // on and nobody speaking.
      // Scored against the one marked take available, at 0.015 this vetoed
      // eighteen gestures including the correct trill and the correct whistle.
      // Widening the measure beyond aperture (see ACTIVITY_SIGNALS) brought
      // that down to four and let both correct detections through, but "four"
      // is still four rejections whose correctness nobody can check, from a
      // threshold fitted to a single recording with music playing in it.
      //
      // What settles it is a pair of takes that do not exist yet: a quiet room
      // with the speaker performing, and the same room with the speaker sitting
      // still while something else makes noise. Until then this stays where the
      // evidence puts it and no further.
      ACTIVITY_MIN: 0.015,
      // Minimum span the activity is judged over. A click is 15 ms long and a
      // camera delivers a frame every 33, so there is nothing inside a
      // transient to look at — the question is whether the speaker was doing
      // anything around then.
      ACTIVITY_WINDOW: 300,
      // Busier than that, with the mouth shut, is lips doing the work.
      BILABIAL_ACTIVITY: 0.03,
      // Should a plosive with no confirmed lip release be dropped rather than
      // merely doubted?
      //
      // This is the one open question Phase 6 could not settle from recordings.
      // The detector fires on the stressed syllable of ordinary speech — around
      // sixty gestures in one 90-second take — because "loud, brief, broadband"
      // describes the onset of half the consonants in English. Of those sixty,
      // four had the aperture release behind them. So the veto would cut sixty
      // to four, and nobody can say from a recording how many of the fifty-six
      // were real: a /t/ or a /k/ never closes the lips at all, and would be
      // thrown away with them.
      //
      // Off by default, because that is what the spec asks for and because
      // discarding data is the more expensive mistake. It is a switch rather
      // than an argument because the answer is a thing you can hear in ten
      // seconds with the microphone on, and could not be reached in three
      // sessions of reading numbers.
      RELEASE_REQUIRED: false,
    },
    CONF_WITH_VISION: 1.0,
    CONF_WITHOUT_VISION: 0.6,
    // Place of articulation — audio gives the rate, vision gives the which.
    // JITTER_HIGH is gone: lipJitter separated a trill from silence by a factor
    // of two against a noise floor of the same size, at a frame rate whose
    // Nyquist is below the trill rate. It was never going to work.
    STRETCH_HIGH: 0.5, // alveolar
  },
};

/**
 * Every default, copied before anything can move a slider.
 *
 * Phase 8 mutates `config` in place — that is the whole design, and it is why
 * nothing may capture a threshold in a closure. The cost is that the shipped
 * values stop being recoverable from the running object, and they are needed
 * twice: to offer "reset" on a control that has been dragged somewhere useless,
 * and to record what a session was *actually* tuned to as a diff rather than as
 * a hundred-line dump of numbers that were mostly untouched.
 *
 * A JSON round trip rather than `structuredClone`: this object is plain data by
 * construction — numbers, strings, booleans, arrays of strings — and if that
 * ever stops being true, a tunable pointing at the exception is a bug worth
 * finding rather than cloning around.
 */
export const defaults = JSON.parse(JSON.stringify(config));

export default config;
